/* ============================================================================
   Tour — POSICIONAMENTO DO BALÃO
   ============================================================================
   Isto existe por causa de um bug relatado em 03/08/2026: no tour do adm escolar,
   o passo da seção "Cadastros" — último item de uma sidebar longa — desenhava o
   balão saindo pela borda inferior da tela, levando o botão "Próximo" com ele. O
   tour ficava sem como avançar.

   POR QUE NÃO BASTA CONFIGURAR O JOYRIDE

   O Joyride 2.x desenha com o react-floater, que usa popper.js 1.x. Três camadas
   conspiram aqui:

   1. As opções do popper entram pela prop `options` do react-floater — NÃO por
      `modifiers`. Tudo em `options` que não seja `arrow`/`flip`/`offset` é jogado
      dentro de `modifiers` (ver `initPopper` em react-floater). Passar `modifiers`
      direto é prop desconhecida: some sem erro.
   2. O Joyride tem `DEFAULTS.floaterProps.options.preventOverflow.boundariesElement
      = 'scrollParent'`, e o scroll parent de um item de menu é A SIDEBAR — caixa
      estreita e alta, dentro da qual o balão nunca cabe.
   3. Pior: quando o alvo TEM scroll parent próprio, o Joyride sobrescreve para
      `'window'` numa linha que roda depois do merge — então nenhum valor nosso
      sobrevive. E `'window'`, em popper v1, é o DOCUMENTO INTEIRO, não a área
      visível. Numa página mais alta que a tela isso não impede o balão de sair do
      campo de visão. A "correção" do Joyride resolve scroll container, não tela.

   A SAÍDA é o ponto de extensão do próprio popper: um modifier nosso, que roda
   depois do `preventOverflow` e prende o balão à janela visível. Não briga com a
   biblioteca — usa a API dela.
   ========================================================================== */

/** Respiro entre o balão e a borda da tela, em px. */
export const MARGEM_JANELA = 12;

/**
 * `order` do nosso modifier na fila do popper v1.
 *
 * A vizinhança importa:
 *   preventOverflow (300) -> NÓS (350) -> keepTogether (400) -> arrow (500) -> flip (600)
 *
 * Rodar em 350 é o que faz a SETA ser recalculada com a posição já corrigida —
 * em 500 ela apontaria para o lugar antigo. E o `keepTogether` logo depois só
 * mexe no eixo principal (horizontal, num balão à direita), então não desfaz o
 * ajuste vertical.
 */
export const ORDEM_MANTER_NA_JANELA = 350;

/**
 * Modifier do popper: mantém o balão dentro da área visível, no eixo vertical.
 *
 * As posições que o popper calcula são de PÁGINA, não de tela — por isso a
 * rolagem entra na conta. Sem ela, o limite seria calculado como se a página
 * estivesse sempre no topo.
 *
 * Balão mais alto que a janela não tem posição boa: encostamos no topo visível e
 * deixamos o teto de altura do CSS (`.react-joyride__tooltip` em tour.css) cuidar
 * do resto com rolagem interna.
 *
 * Função PURA em relação ao DOM: a janela entra por parâmetro, e é isso que
 * permite testar o cálculo sem layout — o jsdom não faz layout nenhum.
 *
 * @param {object} data    dados do popper (mutados e devolvidos, como a API pede)
 * @param {object} janela  algo com `innerHeight` e `pageYOffset`
 */
export function manterNaJanela(data, janela = window) {
  const popper = data && data.offsets && data.offsets.popper;
  if (!popper) return data;

  const alturaJanela = janela.innerHeight || 0;
  const rolagem = janela.pageYOffset || 0;
  if (!alturaJanela) return data;

  const limiteTopo = rolagem + MARGEM_JANELA;
  const limiteBase = rolagem + alturaJanela - popper.height - MARGEM_JANELA;

  // Não cabe de jeito nenhum: encosta no topo visível.
  const alvo =
    limiteBase < limiteTopo
      ? limiteTopo
      : Math.min(Math.max(popper.top, limiteTopo), limiteBase);

  if (alvo === popper.top) return data;

  const deslocamento = alvo - popper.top;
  popper.top = alvo;
  // `bottom` acompanha: o applyStyle e o modifier da seta leem os dois.
  popper.bottom += deslocamento;

  return data;
}

/**
 * Folga mínima entre o alvo e as bordas da janela para ele contar como "à vista".
 *
 * Não é só "aparece na tela": um item encostado na borda de baixo aparece e não
 * deixa espaço para o balão. 96px é aproximadamente a altura de um item de menu
 * mais o respiro que o balão precisa para não nascer colado.
 */
export const FOLGA_ALVO = 96;

/** Valores de `overflow-y` que fazem um elemento rolar de verdade. */
const OVERFLOW_ROLAVEL = ["auto", "scroll", "overlay"];

const estiloPadrao = elemento =>
  typeof window !== "undefined" && window.getComputedStyle
    ? window.getComputedStyle(elemento)
    : null;

/**
 * O container de rolagem do alvo, se ele tiver um próprio.
 *
 * **`<body>` e `<html>` NÃO contam**, e é essa exclusão que separa "rolar a
 * sidebar" de "rolar a página". Um item de menu vive dentro de `.c-sidebar-nav`,
 * que tem `overflow-y: auto` (ver @coreui/coreui/scss/_sidebar.scss) — é ele que
 * deve rolar. Já um botão de linha de tabela não tem container próprio, e para ele
 * rolar a página é o comportamento certo.
 *
 * `estiloDe` devolvendo vazio é aceito como rolável: o jsdom não calcula estilo, e
 * exigir `overflow-y` tornaria a regra inverificável em teste.
 */
export function containerRolavel(
  elemento,
  doc = document,
  estiloDe = estiloPadrao
) {
  if (!elemento) return null;

  let atual = elemento.parentElement;

  while (atual && atual !== doc.body && atual !== doc.documentElement) {
    if (atual.scrollHeight > atual.clientHeight) {
      const overflow = estiloDe(atual)?.overflowY;
      if (!overflow || OVERFLOW_ROLAVEL.includes(overflow)) return atual;
    }
    atual = atual.parentElement;
  }

  return null;
}

/**
 * O alvo está confortavelmente dentro da caixa visível?
 *
 * Recebe retângulos em vez de medir, para ser testável: o jsdom não faz layout e
 * devolve tudo zerado. `caixaVisivel` é o container de rolagem quando existe, ou a
 * janela.
 */
export function precisaRolar(retangulo, caixaVisivel) {
  if (!retangulo || !caixaVisivel) return false;

  const altura = caixaVisivel.bottom - caixaVisivel.top;
  if (altura <= 0) return false;

  // ALVO QUE JÁ PREENCHE A ÁREA VISÍVEL não tem para onde ser trazido: ele está à
  // vista por definição. Rolar aqui seria rolar até o MEIO dele, e é isso que
  // fazia a página andar sozinha no tour do professor — o passo `lista-turmas`
  // aponta para `#list, .animated.fadeIn`, o contêiner da página inteira.
  //
  // No roteiro do aluno o caso não existe: todo alvo dele é item de menu ou badge
  // do header, sempre menor que a tela. Era essa a diferença entre os dois tours.
  const preencheTudo =
    retangulo.top <= caixaVisivel.top &&
    retangulo.bottom >= caixaVisivel.bottom;
  if (preencheTudo) return false;

  return (
    retangulo.top < caixaVisivel.top + FOLGA_ALVO ||
    retangulo.bottom > caixaVisivel.bottom - FOLGA_ALVO
  );
}

/** Quanto o container precisa rolar para o alvo ficar no meio dele. */
export function deslocamentoParaCentralizar(
  retanguloAlvo,
  retanguloContainer,
  alturaVisivel
) {
  const centroAlvo = retanguloAlvo.top + retanguloAlvo.height / 2;
  const centroVisivel = retanguloContainer.top + alturaVisivel / 2;

  return centroAlvo - centroVisivel;
}

/**
 * Traz o alvo para o meio da área visível, se ele não estiver à vista.
 *
 * ISTO ERA A PENDÊNCIA QUE FALTAVA. Abrir a sidebar e abrir o dropdown põem o
 * alvo no fluxo, mas não o trazem para a área visível: numa sidebar longa — e no
 * celular a tela é curta, então "longa" é qualquer menu de verdade — o item fica
 * abaixo da dobra. O balão então nasce ancorado num elemento fora de vista, com a
 * seta apontando para o nada. Foi o sintoma no "Cadastros" do adm escolar.
 *
 * **NÃO usa `scrollIntoView` quando o alvo tem container próprio**, e isso é o
 * ponto: `scrollIntoView` rola TODOS os ancestrais roláveis, inclusive o
 * documento. Era o que fazia o tour do adm escolar arrastar a página inteira
 * enquanto o do professor só mexia na sidebar — a diferença estava só em quão
 * fundo o alvo ficava, não em intenção. Mexendo no `scrollTop` do container, a
 * página nunca é tocada.
 *
 * Sem container próprio (botão de linha de tabela, por exemplo) o alvo está no
 * fluxo da página, e aí rolar a página é o certo — só nesse caso o
 * `scrollIntoView` entra. `block: "center"` e não `"nearest"`, porque `nearest`
 * para na borda, que é justamente onde não há espaço para o balão; e rolagem
 * instantânea, porque o Joyride mede a posição logo depois.
 *
 * @returns {boolean} rolou?
 */
export function rolarAteAlvo(elemento, janela = window, doc = document) {
  if (!elemento || typeof elemento.getBoundingClientRect !== "function") {
    return false;
  }

  const retangulo = elemento.getBoundingClientRect();
  const container = containerRolavel(elemento, doc);

  if (container) {
    const retanguloContainer = container.getBoundingClientRect();
    const visivel = {
      top: retanguloContainer.top,
      bottom: retanguloContainer.top + container.clientHeight
    };

    if (!precisaRolar(retangulo, visivel)) return false;

    container.scrollTop += deslocamentoParaCentralizar(
      retangulo,
      retanguloContainer,
      container.clientHeight
    );
    return true;
  }

  if (typeof elemento.scrollIntoView !== "function") return false;
  if (!precisaRolar(retangulo, { top: 0, bottom: janela.innerHeight || 0 })) {
    return false;
  }

  elemento.scrollIntoView({ block: "center", inline: "nearest" });
  return true;
}

/**
 * `floaterProps` do Joyride.
 *
 * `preventOverflow` fica declarado mesmo sabendo que o Joyride pode sobrescrever
 * o `boundariesElement`: quando o alvo NÃO tem scroll parent próprio (um botão no
 * meio da página, por exemplo), este valor é o que vale, e `viewport` é o certo.
 * O nosso modifier cobre o resto.
 */
export const CONFIG_FLOATER = {
  disableAnimation: true,
  options: {
    preventOverflow: { boundariesElement: "viewport", padding: MARGEM_JANELA },
    manterNaJanela: {
      enabled: true,
      order: ORDEM_MANTER_NA_JANELA,
      fn: manterNaJanela
    }
  }
};
